import _ from 'lodash/fp'
import { all, call, put, takeLatest } from 'redux-saga/effects'
import gql from 'graphql-tag'
import { apolloClient } from '@fe/business/src/apollo'

import { types, actions } from '../reducers/sponsors'
import { getCurrentClubId } from './network'


export const GET_SPONSORS_BY_CLUB = gql`
  query SponsorsByClub($clubId: ID!) {
    sponsorsByClub(input: { clubId: $clubId }) {
      id
      name
      url
      logo
      clubId
      logoFileName
      resourceUrl
      description
    }
  }
`

const CREATE_SPONSOR = gql`
  mutation CreateSponsor($input: CreateSponsorInput!) {
    createSponsor(input: $input) {
      id
      name
      url
      logo
      clubId
    }
  }
`

const UPLOAD_SPONSOR_LOGO = gql`
  mutation UploadSponsorLogo($input: UploadSponsorLogoInput!) {
    uploadSponsorLogo(input: $input) {
      id
      logo
    }
  }
`

const DELETE_SPONSOR = gql`
  mutation DeleteSponsor($input: DeleteSponsorInput!) {
    deleteSponsor(input: $input)
  }
`

const UPDATE_SPONSOR = gql`
  mutation UpdateSponsor($input: UpdateSponsorInput!) {
    updateSponsor(input: $input) {
      id
      name
      url
      logo
      clubId
    }
  }
`

// ImageUpload's cropper hands back a bare base64 PNG string (no data URI prefix) -
// convert it to a Blob so it can be sent as a real multipart Upload variable
const base64ToBlob = (base64, contentType = 'image/png') => {
  const byteCharacters = atob(base64)
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: contentType })
}

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

export function * loadSponsorsEffect () {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.query], {
      query: GET_SPONSORS_BY_CLUB,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    const sponsors = response?.data?.sponsorsByClub ?? []
    return sponsors
  } catch (error) {
    return { error }
  }
}

export function * saveSponsorEffect (sponsor) {
  const { id, name, url } = sponsor
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: UPDATE_SPONSOR,
      variables: { input: { clubId, sponsorId: id, name, url } }
    })
    return _.get('data.updateSponsor', response)
  } catch (error) {
    return { error }
  }
}

export function * createSponsorEffect ({ name, url, club_id: clubId }) {
  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: CREATE_SPONSOR,
      variables: { input: { clubId, name, url } }
    })
    return _.get('data.createSponsor', response)
  } catch (error) {
    return { error }
  }
}

export function * saveSponsorLogoEffect (sponsor) {
  const { id, logo } = sponsor
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: UPLOAD_SPONSOR_LOGO,
      variables: { input: { clubId, sponsorId: id, image: base64ToBlob(logo) } }
    })
    return _.get('data.uploadSponsorLogo', response)
  } catch (error) {
    return { error }
  }
}

export function * deleteSponsorEffect (sponsorId) {
  try {
    const clubId = yield call(getCurrentClubId)
    yield call([apolloClient, apolloClient.mutate], {
      mutation: DELETE_SPONSOR,
      variables: { input: { clubId, sponsorId } }
    })
    return {}
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function * loadSponsorsFlow () {
  const sponsors = yield call(loadSponsorsEffect)

  if (sponsors.error) {
    yield put(actions.loadError(sponsors))
  } else {
    yield put(actions.loadSuccess(sponsors))
  }

  yield put(actions.loadRequestDone())
}

export function * createSponsorFlow ({ payload: { name, url, logo } }) {
  const clubId = yield call(getCurrentClubId)
  const sponsor = yield call(createSponsorEffect, { name, url, logo, club_id: clubId })
  const error = _.get('error', sponsor)
  if (error) {
    yield put(actions.createError(error))
  } else {
    yield call(saveSponsorLogoEffect, _.set('logo', logo, sponsor))

    const createdSponsor = {
      ...sponsor,
      clubId: sponsor.club_id ?? sponsor.clubId ?? clubId,
      logo: true
    }
    
    yield put(actions.createSuccess(createdSponsor))
  }
}

export function * saveSponsorFlow ({ payload: sponsor }) {
  const result = yield call(saveSponsorEffect, sponsor)

  if (typeof sponsor.logo === 'string') {
    yield call(saveSponsorLogoEffect, sponsor)

    const savedSponsor = {
      ...result,
      clubId: result.club_id ?? result.clubId,
      logo: true
    }

    yield put(actions.saveDone(savedSponsor))
  } else {
    yield put(actions.saveDone(result))
  }
}

export function * deleteSponsorFlow ({ payload: sponsorId }) {
  yield call(deleteSponsorEffect, sponsorId)
  yield put(actions.deleteDone(sponsorId))
}

export default function * () {
  return yield all([
    takeLatest(types.CREATE_REQUEST, createSponsorFlow),
    takeLatest(types.LOAD_REQUEST, loadSponsorsFlow),
    takeLatest(types.SAVE_REQUEST, saveSponsorFlow),
    takeLatest(types.DELETE_REQUEST, deleteSponsorFlow)
  ])
}
